import { Mail, Phone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAccountant } from "@/lib/auth";

export function Help() {
  const { data: accountant } = useAccountant();

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <img
          src="/taxr-logo.png"
          alt=""
          aria-hidden="true"
          className="h-8 w-auto opacity-90"
        />
        <h1 className="text-xl font-semibold">Help & support</h1>
      </div>

      {/* Welcome video — mp4 listed first because iOS Safari has */}
      {/* historically mis-handled webm; matches the marketing site's */}
      {/* demo-video partial source order. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Welcome to Taxr</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="aspect-video w-full overflow-hidden rounded-lg bg-foreground">
            <video
              poster="/videos/taxr-accountant-walkthrough-poster.jpg"
              controls
              playsInline
              preload="metadata"
              className="h-full w-full object-contain"
            >
              <source
                src="/videos/taxr-accountant-walkthrough.mp4"
                type="video/mp4"
              />
              <source
                src="/videos/taxr-accountant-walkthrough.webm"
                type="video/webm"
              />
            </video>
          </div>
        </CardContent>
      </Card>

      {/* Getting started */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Getting started</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>
              <strong className="text-foreground">Invite your clients</strong>: paste their email addresses on the invite page. They'll receive instructions to download the app.
            </li>
            <li>
              <strong className="text-foreground">Tune your category list</strong>: open Settings, then Expense categories. Taxr's AI uses this list to classify every client receipt, so the more specific it is, the less manual cleanup you do. New clients inherit it when they accept your invite.
            </li>
            <li>
              <strong className="text-foreground">Clients scan receipts</strong>: they photograph receipts on their phone. Taxr extracts the data automatically.
            </li>
            <li>
              <strong className="text-foreground">Review & categorise</strong>: open any client to review receipts, correct any AI errors, and assign categories.
            </li>
            <li>
              <strong className="text-foreground">Export at tax time</strong>: use the Export button inside each client to download receipts as Excel or PDF with a custom date range.
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Contact support */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contact support</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {accountant?.support_phone ? (
            <a
              href={`tel:${accountant.support_phone.replace(/\s+/g, "")}`}
              className="flex items-center gap-2 text-sm hover:underline"
            >
              <Phone className="size-4 text-muted-foreground" />
              {accountant.support_phone}
            </a>
          ) : (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="size-4" />
              Support phone coming soon
            </p>
          )}
          <a
            href="mailto:support@taxr.io"
            className="flex items-center gap-2 text-sm hover:underline"
          >
            <Mail className="size-4 text-muted-foreground" />
            support@taxr.io
          </a>
        </CardContent>
      </Card>
    </div>
  );
}
